import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { fmtError } from "@/lib/api";
import { fmtINR } from "@/lib/format";
import { downloadFile } from "@/lib/download";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Download, FileText } from "lucide-react";
import { toast } from "sonner";

const STATUS_OPTS = ["draft", "sent", "accepted", "rejected", "expired"];
const STATUS_COLOR = {
  draft: "bg-zinc-100 text-zinc-700 border-zinc-200",
  sent: "bg-amber-50 text-amber-800 border-amber-200",
  accepted: "bg-primary-50 text-primary-700 border-primary-200",
  rejected: "bg-red-50 text-red-700 border-red-200",
  expired: "bg-stone-100 text-stone-500 border-stone-200",
};

// Admin-only: every quotation across the org, filterable by salesperson,
// date range and status, with direct CSV export.
export default function QuotationsReport({ users }) {
  const [ownerId, setOwnerId] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [status, setStatus] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  const buildParams = () => {
    const params = {};
    if (ownerId) params.owner_id = ownerId;
    if (from) params.from_date = new Date(from).toISOString();
    if (to) params.to_date = new Date(to + "T23:59:59").toISOString();
    if (status) params.status = status;
    return params;
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/reports/quotations", { params: buildParams() });
      setData(data);
    } catch (err) { toast.error(fmtError(err)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [ownerId, from, to, status]); // eslint-disable-line react-hooks/exhaustive-deps

  const downloadCsv = async () => {
    setDownloading(true);
    try {
      await downloadFile("/reports/export", { kind: "quotations", ...buildParams() }, "renviro_quotations.csv");
    } catch (err) { toast.error(fmtError(err)); } finally { setDownloading(false); }
  };

  return (
    <Card className="rounded-3xl border-border/60 shadow-soft mb-6" data-testid="quotations-report">
      <CardContent className="p-4 sm:p-6">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
          <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground flex items-center gap-1.5">
            <FileText className="h-3 w-3" /> Quotation report (admin)
          </p>
          <Button onClick={downloadCsv} disabled={downloading} variant="outline" className="rounded-2xl h-9" data-testid="quotations-report-download">
            <Download className="h-4 w-4 mr-1.5" /> {downloading ? "Preparing…" : "Download CSV"}
          </Button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.16em] font-bold text-muted-foreground">Salesperson</Label>
            <Select value={ownerId || "any"} onValueChange={(v) => setOwnerId(v === "any" ? "" : v)}>
              <SelectTrigger className="rounded-2xl h-10 mt-1" data-testid="quotations-report-owner"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-2xl">
                <SelectItem value="any">All salespersons</SelectItem>
                {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.16em] font-bold text-muted-foreground">From</Label>
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="rounded-2xl h-10 mt-1" data-testid="quotations-report-from" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.16em] font-bold text-muted-foreground">To</Label>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="rounded-2xl h-10 mt-1" data-testid="quotations-report-to" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.16em] font-bold text-muted-foreground">Status</Label>
            <Select value={status || "any"} onValueChange={(v) => setStatus(v === "any" ? "" : v)}>
              <SelectTrigger className="rounded-2xl h-10 mt-1" data-testid="quotations-report-status"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-2xl">
                <SelectItem value="any">Any status</SelectItem>
                {STATUS_OPTS.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {loading || !data ? (
          <Skeleton className="h-40 rounded-2xl" />
        ) : (
          <>
            <div className="flex flex-wrap gap-4 mb-3 text-sm">
              <span><strong data-testid="quotations-report-count">{data.count}</strong> quotation{data.count === 1 ? "" : "s"}</span>
              <span>Total value <strong className="text-primary-700">{fmtINR(data.total_value)}</strong></span>
            </div>
            {data.rows.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">No quotations match these filters.</p>
            ) : (
              <div className="space-y-2 max-h-[480px] overflow-y-auto pr-1">
                {data.rows.map((r) => (
                  <Link key={r.id} to={`/quotations/${r.id}`} className="block p-3 rounded-2xl border border-border/60 hover:bg-muted/30 tap transition" data-testid={`quotations-report-row-${r.id}`}>
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-mono text-xs font-bold tracking-wider">{r.number}</p>
                          <Badge variant="outline" className={`text-[10px] uppercase tracking-wider ${STATUS_COLOR[r.status] || ""}`}>{r.status}</Badge>
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5 truncate">
                          {[r.client_company, r.salesperson && `by ${r.salesperson}`, r.date].filter(Boolean).join(" · ")}
                        </p>
                      </div>
                      <p className="font-semibold text-sm tabular-nums shrink-0">{fmtINR(r.grand_total)}</p>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
